import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { BarChart3, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { MediaPicker } from '@/components/media/MediaPicker';
import { useAuth } from '@/lib/auth/useAuth';
import type { MediaRow } from '@/routes/media/types';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import { FacultyStatisticsScreen } from './FacultyStatisticsScreen';
import { FACULTY_ACCENTS, type FacultyAccent, type FacultyInput, type FacultyRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/faculties`, `/faculties/:id`, and
 * `/faculties/:id/statistics`.
 *
 * Four records that change once a decade, and the only module where §3's scope
 * is the record's own id: "faculties.update ✔ (own only)". The list is *not*
 * narrowed — every role that picks a faculty on another screen needs to read it
 * — so the refusal lands on the editor rather than by hiding rows.
 */
export function FacultiesPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  if (rest === '') {
    return <List />;
  }

  // §2 nests the statistics screen under the faculty it belongs to, which is
  // also where an editor looks for it.
  const [id, section] = rest.split('/');

  if (section === 'statistics' && id) {
    return <FacultyStatisticsScreen facultyId={id} />;
  }

  return <Editor recordId={id ?? 'new'} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const columns: Column<FacultyRow>[] = [
    {
      key: 'name',
      label: t('academic.faculties.columns.name'),
      render: (row) => (
        <div className="inline-flex min-w-56 items-center gap-2">
          {/* §1.4's accent, as the swatch it drives on the public card. */}
          <span
            aria-hidden="true"
            className="inline-block h-3 w-3 shrink-0 rounded-full border border-subtle"
            style={{ backgroundColor: `var(--accent-${row.accent}, transparent)` }}
          />
          <span>
            <span className="block font-semibold text-primary">{valueFor(row.name, locale)}</span>
            <span className="block text-caption text-muted">
              {valueFor(row.short_name, locale)}
            </span>
          </span>
        </div>
      ),
    },
    {
      key: 'dean',
      label: t('academic.faculties.columns.dean'),
      render: (row) => <span className="text-caption text-secondary">{row.dean?.name ?? '—'}</span>,
    },
    {
      key: 'counts',
      label: t('academic.faculties.columns.counts'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {t('academic.faculties.countsValue', {
            programs: row.programs_count ?? 0,
            departments: row.departments_count ?? 0,
          })}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'translations',
      label: t('news.columns.translations'),
      render: (row) => (
        <span className="text-caption uppercase text-secondary">
          {row.translations_available.join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'sort_order',
      label: t('content.fields.order'),
      sortable: true,
      align: 'end',
      render: (row) => row.sort_order,
    },
  ];

  return (
    <ResourceListScreen
      resource="faculties"
      title={t('nav.items.faculties')}
      intro={t('academic.faculties.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.name, locale)}
      defaultSort="sort_order"
      emptyTitle={t('academic.faculties.empty.title')}
      emptyBody={t('academic.faculties.empty.body')}
      newLabel={t('academic.faculties.new')}
      deleteTitle={t('academic.faculties.delete.title')}
      deleteBody={(label) => t('academic.faculties.delete.body', { label })}
    />
  );
}

interface Fields {
  name: TranslatedValue;
  short_name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  body: TranslatedValue;
  accent: FacultyAccent;
  icon: string;
  cover_media_id: number | null;
  cover: MediaRow | null;
  dean_staff_id: string;
  email: string;
  phone: string;
  room: string;
  established_year: string;
  sort_order: number;
  status: FacultyRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, user } = useAuth();

  const mayPublish = can('faculties.publish');

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<FacultyRow, Fields, FacultyInput>({
      resource: 'faculties',
      recordId,
      empty,
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'name',
        'short_name',
        'slug',
        'description',
        'body',
        'accent',
        'icon',
        'cover_media_id',
        'dean_staff_id',
        'email',
        'phone',
        'room',
        'established_year',
        'sort_order',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  /*
   * §3 — `faculties.view` is granted to four roles unscoped, `faculties.update`
   * only "(own only)" for a Faculty Admin. So this screen may *show* another
   * faculty and may not save it, and `FacultyPolicy::update()` says so — on
   * submit, after the editing is done.
   *
   * That is the wrong moment. A Faculty Admin opening the faculty next to
   * theirs got a live form, a working "submit for review" button and a 403
   * once they had typed. §6.3 asks for errors that are actionable; the
   * actionable version of this one is not offering the edit.
   *
   * `user.faculty` is null for every role that is not scoped, which is exactly
   * the set that may edit any of them.
   */
  const mayEdit = user?.faculty == null || record == null || user.faculty.id === record.id;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('academic.faculties.new') : t('academic.faculties.edit')}
        </h1>

        <div className="flex flex-wrap gap-2">
          {/* §2's statistics screen, reached from the faculty it belongs to. */}
          {record ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft={<BarChart3 size={16} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => navigate(`/faculties/${record.id}/statistics`)}
            >
              {t('academic.statistics.title')}
            </Button>
          ) : null}

          <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/faculties')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      <FormError message={formError} />

      {mayEdit ? null : (
        <p className="mb-6 flex items-start gap-2 rounded-card border border-subtle bg-surface-alt p-4 text-body-sm text-secondary">
          <Lock size={16} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
          {t('academic.faculties.otherFaculty')}
        </p>
      )}

      {/*
        `contents` so the fieldset disables without becoming a box in the grid.
        A native `disabled` fieldset switches off every form control inside it,
        which is both fewer props than threading a flag through twenty fields
        and the thing screen readers announce.
      */}
      <fieldset disabled={!mayEdit} className="contents">
        <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
          <div className="space-y-5">
            <TranslatableField
              label={t('academic.faculties.fields.name')}
              name="name"
              value={values.name}
              onChange={(next) => setValue('name', next, { shouldDirty: true })}
              error={formState.errors.name?.message}
            />

            <TranslatableField
              label={t('academic.faculties.fields.shortName')}
              hint={t('academic.faculties.fields.shortNameHint')}
              name="short_name"
              value={values.short_name}
              onChange={(next) => setValue('short_name', next, { shouldDirty: true })}
              error={formState.errors.short_name?.message}
            />

            <SlugField
              value={values.slug}
              onChange={(next) => setValue('slug', next, { shouldDirty: true })}
              original={record?.slug}
              published={values.status === 'published'}
              error={formState.errors.slug?.message}
            />

            <TranslatableField
              label={t('content.fields.description')}
              hint={t('academic.faculties.fields.descriptionHint')}
              name="description"
              multiline
              rows={3}
              value={values.description}
              onChange={(next) => setValue('description', next, { shouldDirty: true })}
              error={formState.errors.description?.message}
            />

            <RichTextField
              label={t('content.fields.body')}
              value={values.body}
              onChange={(next) => setValue('body', next, { shouldDirty: true })}
              // TipTap is a contenteditable rather than a form control, so the
              // fieldset above does not reach it.
              disabled={!mayEdit}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label={t('academic.faculties.fields.accent')}
                hint={t('academic.faculties.fields.accentHint')}
                {...register('accent')}
                error={formState.errors.accent?.message}
                options={FACULTY_ACCENTS.map((accent) => ({
                  value: accent,
                  label: t(`vocational.programs.accents.${accent}`),
                }))}
              />

              <Input
                {...register('icon')}
                label={t('academic.faculties.fields.icon')}
                hint={t('academic.faculties.fields.iconHint')}
                error={formState.errors.icon?.message}
              />

              <Input
                {...register('dean_staff_id')}
                type="number"
                min={1}
                label={t('academic.faculties.fields.dean')}
                hint={t('academic.faculties.fields.deanHint')}
                error={formState.errors.dean_staff_id?.message}
              />

              <Input
                {...register('established_year')}
                type="number"
                min={1990}
                label={t('academic.faculties.fields.established')}
                error={formState.errors.established_year?.message}
              />

              <Input
                {...register('email')}
                type="email"
                label={t('academic.faculties.fields.email')}
                error={formState.errors.email?.message}
              />

              <Input
                {...register('phone')}
                type="tel"
                label={t('academic.faculties.fields.phone')}
                error={formState.errors.phone?.message}
              />

              <Input
                {...register('room')}
                label={t('academic.faculties.fields.room')}
                error={formState.errors.room?.message}
              />

              {/*
                §4.5's picker, replacing the text box an editor typed a path
                into. The image is chosen from the shared library or uploaded
                in place — either way it lands in the library, which is what
                makes the same photograph reusable across records.
              */}
              <MediaPicker
                label={t('content.fields.cover')}
                hint={t('content.fields.coverHint')}
                value={values.cover}
                onChange={(mediaId, media) => {
                  setValue('cover_media_id', mediaId, { shouldDirty: true });
                  setValue('cover', media, { shouldDirty: true });
                }}
                error={formState.errors.cover_media_id?.message}
                disabled={!mayEdit}
              />

              <Input
                {...register('sort_order', { valueAsNumber: true })}
                type="number"
                min={0}
                label={t('content.fields.order')}
                hint={t('content.fields.orderHint')}
              />
            </div>

            {record?.dean ? (
              <p className="text-caption text-secondary">
                {t('academic.faculties.currentDean', { name: record.dean.name })}
              </p>
            ) : null}
          </div>

          <div className="space-y-4">
            <PublishingSidebar
              resource="faculties"
              status={values.status}
              onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
              publishedAt={values.published_at}
              onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
              updatedAt={record?.updated_at}
              updatedBy={record?.updated_by}
              pendingReview={record?.pending_review}
              saving={isSubmitting}
              disabled={!mayEdit}
            />

            <TranslationProgress
              fields={{
                name: { label: t('academic.faculties.fields.name'), value: values.name },
                description: { label: t('content.fields.description'), value: values.description },
                body: { label: t('content.fields.body'), value: values.body },
              }}
            />
          </div>
        </div>
      </fieldset>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    name: {},
    short_name: {},
    slug: {},
    description: {},
    body: {},
    accent: 'slate',
    icon: '',
    cover_media_id: null,
    cover: null,
    dean_staff_id: '',
    email: '',
    phone: '',
    room: '',
    established_year: '',
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: FacultyRow): Fields {
  return {
    name: row.name,
    short_name: row.short_name,
    slug: row.slug,
    description: row.description,
    body: row.body,
    accent: row.accent,
    icon: row.icon ?? '',
    cover_media_id: row.cover_media_id ?? null,
    cover: row.cover ?? null,
    dean_staff_id: row.dean_staff_id === null ? '' : String(row.dean_staff_id),
    email: row.email ?? '',
    phone: row.phone ?? '',
    room: row.room ?? '',
    established_year: row.established_year === null ? '' : String(row.established_year),
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): FacultyInput {
  return {
    name: form.name,
    short_name: form.short_name,
    slug: form.slug,
    description: form.description,
    body: form.body,
    accent: form.accent,
    icon: form.icon.trim() === '' ? null : form.icon.trim(),
    cover_media_id: form.cover_media_id,
    dean_staff_id: form.dean_staff_id === '' ? null : Number(form.dean_staff_id),
    email: form.email.trim() === '' ? null : form.email.trim(),
    phone: form.phone.trim() === '' ? null : form.phone.trim(),
    room: form.room.trim() === '' ? null : form.room.trim(),
    established_year: form.established_year === '' ? null : Number(form.established_year),
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    pending_review: mayPublish ? form.pending_review : true,
  };
}
